import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";

const useSocket = (currentMember, spaceId) => {
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!currentMember || !spaceId) return;

    // Initialize socket connection
    const auth = currentMember.guestAccessId
      ? { guestAccessId: currentMember.guestAccessId }
      : { token: currentMember.token };

    socketRef.current = io(API_URL, {
      auth,
      autoConnect: true,
    });

    const socket = socketRef.current;

    // Connection event handlers
    socket.on("connect", () => {
      console.log("Connected to server with ID:", socket.id);
      setIsConnected(true);

      // Join the space room
      socket.emit("JOIN_CHAT_SPACE", {
        spaceId: spaceId,
        memberId: currentMember._id,
      });
    });

    socket.on("disconnect", () => {
      console.log("Disconnected from server");
      setIsConnected(false);
    });

    socket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      setIsConnected(false);
    });

    // Space-related event handlers
    socket.on("joined-space", (data) => {
      console.log("Successfully joined space:", data);
    });

    socket.on("error", (error) => {
      console.error("Socket error:", error);
    });

    // Typing indicators
    socket.on("user-typing", (data) => {
      if (data.isTyping) {
        setTypingUsers((prev) => {
          if (!prev.find((user) => user.memberId === data.memberId)) {
            return [
              ...prev,
              { memberId: data.memberId, memberAlias: data.memberAlias },
            ];
          }
          return prev;
        });
      } else {
        setTypingUsers((prev) =>
          prev.filter((user) => user.memberId !== data.memberId),
        );
      }
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, [currentMember, spaceId]);

  // Helper functions
  const emitTypingStart = () => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit("typing-start", {
        memberAlias: currentMember?.alias || currentMember?.name,
      });
    }
  };

  const emitTypingStop = () => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit("typing-stop");
    }
  };

  const onNewMessage = (callback) => {
    if (socketRef.current) {
      socketRef.current.on("SPACE_NEW_MESSAGE", callback);
    }
  };

  const onMessageDeleted = (callback) => {
    if (socketRef.current) {
      socketRef.current.on("message-deleted", callback);
    }
  };

  const offNewMessage = () => {
    if (socketRef.current) {
      socketRef.current.off("SPACE_NEW_MESSAGE");
    }
  };

  const offMessageDeleted = () => {
    if (socketRef.current) {
      socketRef.current.off("message-deleted");
    }
  };

  return {
    socket: socketRef.current,
    isConnected,
    typingUsers,
    emitTypingStart,
    emitTypingStop,
    onNewMessage,
    onMessageDeleted,
    offNewMessage,
    offMessageDeleted,
  };
};

export default useSocket;
